import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Download, Loader2, Calendar, User, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface ExportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant: any;
}

interface QhRow {
  date: Date;
  dateDebut: string;
  dateFin: string;
  ean: string;
  typeVolume: string;
  volumeKwh: number;
}

function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  try {
    const parts = dateStr.trim().split(' ');
    if (!parts[0]) return null;
    const dateParts = parts[0].split('/');
    if (dateParts.length !== 3) {
      const isoParts = parts[0].split('-');
      if (isoParts.length === 3) {
        const [y, m, d] = isoParts;
        const time = parts[1] || '00:00';
        const dt = new Date(`${y}-${m}-${d}T${time}`);
        return isNaN(dt.getTime()) ? null : dt;
      }
      return null;
    }
    const [day, month, year] = dateParts;
    if (!day || !month || !year) return null;
    const time = parts[1] || '00:00';
    const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${time}`;
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

export function ExportDataModal({ isOpen, onClose, participant }: ExportDataModalProps) {
  const [loading, setLoading] = useState(false);
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [allRows, setAllRows] = useState<QhRow[]>([]);

  const eanCode = participant?.ean_code;

  const availableMonths = useMemo(() => {
    if (!participant?.entry_date) return [];
    const entry = new Date(participant.entry_date);
    const months: string[] = [];
    const now = new Date();
    let cursor = new Date(entry.getFullYear(), entry.getMonth(), 1);
    while (cursor <= now) {
      months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return months.sort();
  }, [participant?.entry_date]);

  useEffect(() => {
    if (!isOpen || !eanCode) return;

    const loadData = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from('monthly_csv_links')
          .select('*')
          .order('year', { ascending: true })
          .order('month_number', { ascending: true });

        if (participant?.sharing_operation_id) {
          query = query.eq('sharing_operation_id', participant.sharing_operation_id);
        }

        const { data, error } = await query;

        if (error) throw error;

        const entryDate = participant?.entry_date ? new Date(participant.entry_date) : null;

        const rows: QhRow[] = [];

        for (const link of data || []) {
          if (!link.csv_url) continue;
          try {
            const result = await new Promise<Papa.ParseResult<any>>((resolve, reject) => {
              Papa.parse(link.csv_url, {
                download: true,
                header: true,
                skipEmptyLines: true,
                complete: resolve,
                error: reject,
              });
            });

            for (const r of result.data) {
              if (!r['Date Début'] || !r['EAN'] || !r['Type de volume']) continue;
              const rawEan = r['EAN'].trim();
              if (rawEan !== eanCode) continue;

              const parsed = parseDate(r['Date Début']);
              if (!parsed) continue;

              if (entryDate && parsed < entryDate) continue;

              const volumeStr = r['Volume (kWh)'];
              const volume = parseFloat(volumeStr ? volumeStr.toString().replace(',', '.') : '0') || 0;

              rows.push({
                date: parsed,
                dateDebut: r['Date Début'],
                dateFin: r['Date Fin'] || '',
                ean: rawEan,
                typeVolume: r['Type de volume'].trim(),
                volumeKwh: volume,
              });
            }
          } catch {
            // skip broken link
          }
        }

        rows.sort((a, b) => a.date.getTime() - b.date.getTime());
        setAllRows(rows);

        if (rows.length > 0) {
          const first = rows[0].date;
          const last = rows[rows.length - 1].date;
          setStartMonth(`${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, '0')}`);
          setEndMonth(`${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}`);
        } else if (availableMonths.length > 0) {
          setStartMonth(availableMonths[0]);
          setEndMonth(availableMonths[availableMonths.length - 1]);
        }
      } catch (err: any) {
        toast.error('Erreur lors du chargement des données: ' + (err.message || err));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, eanCode]);

  const filteredRows = useMemo(() => {
    if (!startMonth || !endMonth) return allRows;
    return allRows.filter(r => {
      const mk = `${r.date.getFullYear()}-${String(r.date.getMonth() + 1).padStart(2, '0')}`;
      return mk >= startMonth && mk <= endMonth;
    });
  }, [allRows, startMonth, endMonth]);

  const handleExport = useCallback(() => {
    if (filteredRows.length === 0) {
      toast.error('Aucune donnée à exporter pour cette période');
      return;
    }

    const volumeTypes: string[] = [];
    const typeSet = new Set<string>();
    for (const r of filteredRows) {
      if (r.typeVolume && !typeSet.has(r.typeVolume)) {
        typeSet.add(r.typeVolume);
        volumeTypes.push(r.typeVolume);
      }
    }
    volumeTypes.sort();

    const grouped = new Map<string, { dateDebut: string; dateFin: string; ean: string; volumes: Record<string, number> }>();
    for (const r of filteredRows) {
      const key = `${r.dateDebut}|${r.dateFin}`;
      if (!grouped.has(key)) {
        grouped.set(key, { dateDebut: r.dateDebut, dateFin: r.dateFin, ean: r.ean, volumes: {} });
      }
      grouped.get(key)!.volumes[r.typeVolume] = (grouped.get(key)!.volumes[r.typeVolume] || 0) + r.volumeKwh;
    }

    const sortedKeys = Array.from(grouped.keys()).sort();

    const header = ['Date Début', 'Date Fin', 'EAN', ...volumeTypes];
    const wsData = [
      header,
      ...sortedKeys.map(key => {
        const g = grouped.get(key)!;
        return [g.dateDebut, g.dateFin, g.ean, ...volumeTypes.map(vt => g.volumes[vt] ?? 0)];
      }),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [
      { wch: 20 }, { wch: 20 }, { wch: 18 },
      ...volumeTypes.map(() => ({ wch: 16 })),
    ];

    const wb = XLSX.utils.book_new();
    const safeName = (participant?.name || 'participant').replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 28);
    XLSX.utils.book_append_sheet(wb, ws, 'Quart-horaire');

    const periodLabel = `${startMonth || 'debut'}_${endMonth || 'fin'}`;
    XLSX.writeFile(wb, `${safeName}_${periodLabel}.xlsx`);
    toast.success(`${sortedKeys.length} lignes exportées`);
  }, [filteredRows, participant, startMonth, endMonth]);

  if (!isOpen) return null;

  const participantName = participant?.groupe
    ? `Groupe ${participant.groupe}`
    : participant?.name || 'Participant';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <Download className="w-6 h-6 text-amber-600" />
              <h3 className="text-xl font-semibold text-neutral-900">
                Exporter les données
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4 mb-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-1">
                <User className="w-4 h-4 text-amber-700" />
                <span className="font-medium text-amber-900">{participantName}</span>
              </div>
              <p className="text-sm text-amber-700">
                EAN: <span className="font-mono">{eanCode || 'Non renseigné'}</span>
              </p>
              {participant?.entry_date && (
                <p className="text-sm text-amber-700 mt-1">
                  Date d'entrée: {new Date(participant.entry_date).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                <span className="ml-3 text-sm text-neutral-600">Chargement des données...</span>
              </div>
            ) : (
              <>
                {allRows.length === 0 ? (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-neutral-50 border border-neutral-200">
                    <AlertCircle className="w-5 h-5 text-neutral-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-neutral-600">
                      Aucune donnée quart-horaire trouvée pour ce participant.
                      Vérifiez que des liens Google Sheet ont été enregistrés et que le code EAN correspond.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-sm text-neutral-600">
                      <Calendar className="w-4 h-4" />
                      <span>{allRows.length} lignes trouvées au total</span>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Mois de début
                      </label>
                      <input
                        type="month"
                        value={startMonth}
                        min={availableMonths[0] || undefined}
                        max={availableMonths[availableMonths.length - 1] || undefined}
                        onChange={(e) => setStartMonth(e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Mois de fin
                      </label>
                      <input
                        type="month"
                        value={endMonth}
                        min={availableMonths[0] || undefined}
                        max={availableMonths[availableMonths.length - 1] || undefined}
                        onChange={(e) => setEndMonth(e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>

                    <div className="text-sm text-neutral-500">
                      {filteredRows.length} lignes dans la période sélectionnée
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 hover:text-neutral-900 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleExport}
              disabled={loading || filteredRows.length === 0}
              className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Exporter Excel</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
